import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, cn, Input, Label, useToast } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Logo } from "@racoon/icons";
import { useSelfRegisterViaLinkMutation } from "~/shared/api/auth/auth.grpc";
import { ConnectProto, ErrorCode, type ApiError } from "@racoon/api";
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from "@racoon/lib";
import { formatPhone, phoneDigits, isCompletePhone } from "~/shared/lib/phone";

const registerSchema = z
  .object({
    first_name: z.string().min(1).max(100),
    last_name: z.string().min(1).max(100),
    email: z
      .union([z.string().email(), z.literal("")])
      .optional()
      .transform((e) => (e === "" ? undefined : e)),
    phone_number: z
      .string()
      .optional()
      .transform((p) => {
        // A bare country code means the field was left untouched.
        if (!p || p.replace(/\D/g, "").length <= 1) return undefined;
        return p;
      })
      .refine((p) => p === undefined || isCompletePhone(p), { message: "phoneInvalid" }),
    password: z.string().min(PASSWORD_MIN_LENGTH, "passwordMinLength").max(PASSWORD_MAX_LENGTH),
    confirm_password: z.string().min(1),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "passwordsMismatch",
    path: ["confirm_password"],
  })
  .refine(
    (data) => {
      const hasEmail = data.email && data.email.trim().length > 0;
      const hasPhone = data.phone_number && data.phone_number.trim().length > 0;

      return hasEmail || hasPhone;
    },
    {
      message: "emailOrPhoneRequired",
      path: ["email"],
    },
  );

type RegisterFormValues = z.infer<typeof registerSchema>;

export const RegisterInviteForm = () => {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const firstNameRef = useRef<HTMLInputElement | null>(null);

  const registrationToken = searchParams.get("token");
  const isValidLink = !!registrationToken;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
    setError,
    setFocus,
    setValue,
    watch,
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "+7 ",
      password: "",
      confirm_password: "",
    },
  });

  const [selfRegisterViaLink, { isLoading }] = useSelfRegisterViaLinkMutation();

  const phoneNumber = watch("phone_number");

  useEffect(() => {
    if (firstNameRef.current) firstNameRef.current.focus();
    else setFocus("first_name");
  }, [setFocus]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("phone_number", formatPhone(e.target.value), { shouldValidate: true });
  };

  const onSubmit = async (values: RegisterFormValues) => {
    if (!registrationToken) {
      toast({
        title: t(Namespace.Errors, "error"),
        description: t(Namespace.Auth, "linkExpired"),
        variant: "destructive",
      });
      return;
    }

    try {
      const digits = values.phone_number ? phoneDigits(values.phone_number) : "";
      // Role and organization come from the registration token, server-side.
      const registeredUser = await selfRegisterViaLink({
        registrationToken,
        firstName: values.first_name,
        lastName: values.last_name,
        // The schema guarantees one of the two; a phone-only sign-up gets the
        // same derived address the admin-side create uses.
        email: values.email || (digits ? `${digits}@phone.kz` : undefined),
        phoneNumber: digits ? `+${digits}` : undefined,
        password: values.password,
      }).unwrap();

      toast({
        title: t(Namespace.Auth, "registrationSuccess"),
        description: t(Namespace.Auth, "registrationSuccessMessage"),
      });

      // Customers live in the client PWA; every other role signs into this platform.
      if (registeredUser.role !== ConnectProto.UserRole.RECEIVER_USER) {
        navigate("/login");
        return;
      }

      const clientPwaUrl = import.meta.env.VITE_CLIENT_PWA_URL;
      try {
        if (!clientPwaUrl) {
          throw new Error("VITE_CLIENT_PWA_URL is not defined");
        }
        // Validate URL
        new URL(clientPwaUrl);
        window.location.href = clientPwaUrl;
      } catch {
        toast({
          title: t(Namespace.Errors, "error"),
          description: t(Namespace.Auth, "invalidRedirectUrl"),
          variant: "destructive",
        });
        // Optionally, log the error for debugging
        // console.error("Invalid VITE_CLIENT_PWA_URL:", err);
      }
    } catch (error) {
      // The global error toaster already surfaced this. Only mark the field the
      // user has to fix — a second toast would repeat what they just read.
      const { code } = error as Partial<ApiError>;
      if (code === ErrorCode.ERROR_CODE_DUPLICATE_EMAIL) {
        setError("email", { type: "server", message: t(Namespace.Auth, "emailExists") });
      } else if (code === ErrorCode.ERROR_CODE_DUPLICATE_PHONE_NUMBER) {
        setError("phone_number", { type: "server", message: t(Namespace.Auth, "phoneExists") });
      }
    }
  };

  const busy = isSubmitting || isLoading;

  if (!isValidLink) {
    return (
      <div className="w-full max-w-md mx-auto p-4 sm:p-6 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur shadow-lg border border-slate-200/60 dark:border-slate-700/40">
        <div className="flex items-center justify-center mb-4 sm:mb-6">
          <Logo className="w-full h-12 sm:h-16" />
        </div>
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">
            {t(Namespace.Auth, "invalidLink")}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4 sm:mb-6">
            {t(Namespace.Auth, "invalidLinkDescription")}
          </p>
          <Button
            id="register-goto-login-button"
            onClick={() => navigate("/login")}
            className="w-full"
          >
            {t(Namespace.Auth, "goToLogin")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto p-4 sm:p-6 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur shadow-lg border border-slate-200/60 dark:border-slate-700/40">
      <div className="flex items-center justify-center mb-4 sm:mb-6">
        <Logo className="w-full h-12 sm:h-16" />
      </div>

      <h2 className="text-center text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">
        {t(Namespace.Auth, "registerAccount")}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="first_name" className="text-sm font-medium">
            {t(Namespace.Auth, "firstName")}
          </Label>
          <Input
            id="first_name"
            type="text"
            placeholder={t(Namespace.Auth, "firstNamePlaceholder")}
            className={cn(
              "w-full",
              errors.first_name && "border-red-500 focus-visible:ring-red-500",
            )}
            {...register("first_name")}
            ref={(el: HTMLInputElement | null) => {
              (register("first_name").ref as any)?.(el);
              firstNameRef.current = el;
            }}
            aria-invalid={!!errors.first_name || undefined}
            aria-describedby={errors.first_name ? "first-name-error" : undefined}
          />
          {errors.first_name && (
            <p id="first-name-error" className="text-xs text-red-600">
              {t(Namespace.Auth, "firstNameRequired")}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="last_name" className="text-sm font-medium">
            {t(Namespace.Auth, "lastName")}
          </Label>
          <Input
            id="last_name"
            type="text"
            placeholder={t(Namespace.Auth, "lastNamePlaceholder")}
            className={cn(
              "w-full",
              errors.last_name && "border-red-500 focus-visible:ring-red-500",
            )}
            {...register("last_name")}
            aria-invalid={!!errors.last_name || undefined}
            aria-describedby={errors.last_name ? "last-name-error" : undefined}
          />
          {errors.last_name && (
            <p id="last-name-error" className="text-xs text-red-600">
              {t(Namespace.Auth, "lastNameRequired")}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">
            {t(Namespace.Auth, "email")}
          </Label>
          <Input
            id="email"
            type="email"
            placeholder={t(Namespace.Auth, "emailPlaceholder")}
            className={cn("w-full", errors.email && "border-red-500 focus-visible:ring-red-500")}
            {...register("email")}
            aria-invalid={!!errors.email || undefined}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && (
            <p id="email-error" className="text-xs text-red-600">
              {errors.email.type === "server"
                ? errors.email.message
                : t(Namespace.Auth, "emailInvalid")}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone_number" className="text-sm font-medium">
            {t(Namespace.Auth, "phoneNumber")}
          </Label>
          <Input
            id="phone_number"
            type="tel"
            placeholder={t(Namespace.Auth, "phonePlaceholder")}
            className={cn(
              "w-full",
              errors.phone_number && "border-red-500 focus-visible:ring-red-500",
            )}
            value={phoneNumber}
            onChange={handlePhoneChange}
            aria-invalid={!!errors.phone_number || undefined}
            aria-describedby={errors.phone_number ? "phone-error" : undefined}
          />
          {errors.phone_number && (
            <p id="phone-error" className="text-xs text-red-600">
              {errors.phone_number.message === "phoneInvalid"
                ? t(Namespace.Auth, "phoneInvalid")
                : errors.phone_number.message || t(Namespace.Auth, "phoneRequired")}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-sm font-medium">
            {t(Namespace.Auth, "password")}
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder={t(Namespace.Auth, "passwordPlaceholder")}
              className={cn(
                "w-full pr-10",
                errors.password && "border-red-500 focus-visible:ring-red-500",
              )}
              {...register("password")}
              aria-invalid={!!errors.password || undefined}
              aria-describedby={errors.password ? "password-error" : undefined}
            />
            <button
              id="register-toggle-password-button"
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              aria-label={
                showPassword
                  ? t(Namespace.Auth, "hidePasswordAria")
                  : t(Namespace.Auth, "showPasswordAria")
              }
              onClick={() => setShowPassword((s) => !s)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p id="password-error" className="text-xs text-red-600">
              {t(Namespace.Auth, "passwordMinLength", { min: PASSWORD_MIN_LENGTH })}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm_password" className="text-sm font-medium">
            {t(Namespace.Auth, "confirmPassword")}
          </Label>
          <div className="relative">
            <Input
              id="confirm_password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t(Namespace.Auth, "confirmPasswordPlaceholder")}
              className={cn(
                "w-full pr-10",
                errors.confirm_password && "border-red-500 focus-visible:ring-red-500",
              )}
              {...register("confirm_password")}
              aria-invalid={!!errors.confirm_password || undefined}
              aria-describedby={errors.confirm_password ? "confirm-password-error" : undefined}
            />
            <button
              id="register-toggle-confirm-password-button"
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              aria-label={
                showConfirmPassword
                  ? t(Namespace.Auth, "hidePasswordAria")
                  : t(Namespace.Auth, "showPasswordAria")
              }
              onClick={() => setShowConfirmPassword((s) => !s)}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirm_password && (
            <p id="confirm-password-error" className="text-xs text-red-600">
              {errors.confirm_password.message === "passwordsMismatch"
                ? t(Namespace.Auth, "passwordsMismatch")
                : t(Namespace.Auth, "confirmPasswordRequired")}
            </p>
          )}
        </div>

        <Button
          id="register-submit-button"
          type="submit"
          className="w-full"
          disabled={busy || !isDirty || !isValid}
        >
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t(Namespace.Auth, "registering")}
            </span>
          ) : (
            t(Namespace.Auth, "register")
          )}
        </Button>
      </form>
    </div>
  );
};
